import React, { useMemo, useState } from 'react'
import { Container } from './styles'
import { MotherTimelineLabel } from './Label'
import { ChangeTimelineButton } from './ChangeTimelineButton'
import { BabySelector } from './BabySelector'
import { BabyNotFoundMessage } from '../BabyNotFoundMessage'
import { useMaternalInfantHealthProcedures, usePatient } from '~/context'

import { hasObjectValue, toFalse } from '~/utils'
import { getIndexSelectedBaby, isMultiplePregnancy, isSingletonPregnancy } from '~/interfaces'
import {
  babyGestationDevelopmentStageLabel,
  motherGestationDevelopmentStageLabel
} from '~/utils/dashboard/timeline/maternalInfant'
import { useMaternalInfantViewSettings } from '~/hooks'

const HIDE = false
const SHOW = true

export const TimelineSelector = (): JSX.Element => {
  const [babyNotFoundMessageVisibility, setBabyNotFoundMessageVisibility] = useState<boolean>(HIDE)
  const showBabyNotFoundMessage = () => setBabyNotFoundMessageVisibility(SHOW)

  const { patient } = usePatient()

  const {
    selectedPregnancy,
    selectedBaby,
    selectedTimeline,
    updateSelectedBaby,
    resetFilterTags,
    changeToBabyTimeline,
    isMotherTimelineSelected,
    isBabyTimelineSelected,
    thereAreNoSelectedPregnancy,
    updateSelectedTimeline
  } = useMaternalInfantHealthProcedures()

  const { updateMaternalInfantViewSettings } = useMaternalInfantViewSettings()

  const motherPeriodLabel = useMemo(() => motherGestationDevelopmentStageLabel(selectedPregnancy), [selectedPregnancy])
  const babyPeriodLabel = useMemo(() => babyGestationDevelopmentStageLabel(selectedPregnancy), [selectedPregnancy])

  const [openBabySelector, setOpenBabySelector] = useState<boolean>(false)

  const getTooltipText = () => {
    if (isBabyTimelineSelected) return `Visualizar Perfil da ${motherPeriodLabel}`

    if (!hasObjectValue({ ...selectedPregnancy })) return ''

    if (isMultiplePregnancy(selectedPregnancy)) {
      const babyIndex = getIndexSelectedBaby(selectedPregnancy, selectedBaby)
      return `Visualizar Perfil do ${babyPeriodLabel} ${babyIndex}`
    }

    if (isSingletonPregnancy(selectedPregnancy)) return `Visualizar Perfil do ${babyPeriodLabel}`

    return 'N/A'
  }

  const closeBabySelector = () => setOpenBabySelector(toFalse)

  const handleChangeSelectedBaby = (childId: number): void => {
    updateSelectedBaby(childId)
    changeToBabyTimeline()
    closeBabySelector()

    updateMaternalInfantViewSettings({
      medicalRecord: patient.medicalRecord.toString(),
      selectedPregnancy,
      selectedBaby: childId,
      selectedTimeline: 'baby'
    })
  }

  const toggleSelectedTimeline = () => {
    return selectedTimeline === 'mother' ? 'baby' : 'mother'
  }

  const handleOnChangeTimeline = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault()

    const hasAnySelectedBaby = selectedBaby ?? false
    if (hasAnySelectedBaby) {
      const newSelectedTimeline = toggleSelectedTimeline()
      updateSelectedTimeline(newSelectedTimeline)
      resetFilterTags()
      updateMaternalInfantViewSettings({
        medicalRecord: patient?.medicalRecord.toString(),
        selectedPregnancy,
        selectedBaby,
        selectedTimeline: newSelectedTimeline
      })
      return
    }

    showBabyNotFoundMessage()
  }

  if (thereAreNoSelectedPregnancy()) return <div></div>

  return (
    <>
      <Container>
        <div className="babyMotherInfos">
          <MotherTimelineLabel isActive={isMotherTimelineSelected}>{motherPeriodLabel}</MotherTimelineLabel>

          <ChangeTimelineButton tooltipText={getTooltipText()} onClick={handleOnChangeTimeline} />

          <BabySelector
            babies={selectedPregnancy.children}
            babiesPeriodLabel={babyPeriodLabel}
            selectedBaby={selectedBaby}
            indexSelectedBaby={getIndexSelectedBaby(selectedPregnancy, selectedBaby)}
            isMultiplePregnancy={isMultiplePregnancy(selectedPregnancy)}
            isActive={isBabyTimelineSelected}
            onClick={() => setOpenBabySelector(!openBabySelector)}
            openBabySelector={openBabySelector}
            onSelectBaby={handleChangeSelectedBaby}
          />
        </div>
      </Container>

      <BabyNotFoundMessage show={babyNotFoundMessageVisibility} onClose={setBabyNotFoundMessageVisibility} />
    </>
  )
}
